import {calculateErrorMessage} from 'common/helpers'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {addBotMessage, addUserMessage} from 'setup/redux/Actions/Chatbot/chatbot'

import AttendanceDatePicker from './components/chat/AttendanceDatePicker.tsx'
import AttendanceInputSuggestionPills from './components/chat/AttendanceInputSuggestionPills.tsx'
import AttendanceMonthYearInput from './components/chat/AttendanceMonthYearInput.tsx'
import AttendanceSchoolMultiSelect from './components/chat/AttendanceSchoolMultiSelect.tsx'
import AttendanceSchoolSelect from './components/chat/AttendanceSchoolSelect.tsx'
import AttendanceStudentMultiSelect from './components/chat/AttendanceStudentMultiSelect.tsx'
import BloodGroupSelect from './components/chat/BloodGroupSelect.tsx'
import ChatbotDatePicker from './components/chat/ChatbotDatePicker.tsx'
import ChatbotDateRangeInput from './components/chat/ChatbotDateRangeInput.tsx'
import CountrySelect from './components/chat/CountrySelect.tsx'
import MenuBubbles from './components/chat/MenuBubbles.tsx'
import OptionChips from './components/chat/OptionChips.tsx'
import {SelectOption} from './components/chat/SelectWithSearch'
import SuggestionBubbles from './components/chat/SuggestionBubbles.tsx'
import {ChatbotMode, showsAttendanceSubmenuAtRoot} from './components/config/chatbotModeConfig'
import {MenuItem} from './components/config/menuConfig'
import {useVoiceCallState} from './components/hooks/useVoiceCallState'
import BotMessage from './components/messages/BotMessage.tsx'
import CallSummaryCard from './components/messages/CallSummaryCard'
import ChatInput from './components/messages/ChatInput.tsx'
import FileUploadInput from './components/messages/FileUploadInput.tsx'
import UserMessage from './components/messages/UserMessage.tsx'
import PromptModal from './components/modal/PromptModal'
import TranscriptionModal from './components/transcription/TranscriptionModal'
import AttendanceBotAvatar from './components/ui/AttendanceBotAvatar'
import ChatbotHeader from './components/ui/ChatbotHeader.tsx'
import ChatbotPoweredBy from './components/ui/ChatbotPoweredBy.tsx'
import EmptyState from './components/ui/EmptyState.tsx'
import TypingIndicator from './components/ui/TypingIndicator.tsx'
import VoiceModePage from './components/voice/VoiceModePage'
import VoiceSelectionModal from './components/voice/VoiceSelectionModal'
import {useChatbotResolvedTheme} from './hooks/useChatbotResolvedTheme'
import {AssistantCapability} from './modes/assistant/assistantCapabilities'
import {
  AssistantClarificationOption,
  AssistantSuggestion,
} from './modes/assistant/assistantResponseContract'
import {ChatSurface, filterClarificationForSurface} from './modes/assistant/chatSurface'
import {
  AttendanceInputSuggestion,
  attendancePillsFitLastTurn,
  getWelcomeAttendanceSuggestions,
  isAttendanceAssistiveUiActive,
} from './modes/attendance/attendanceInputSuggestions'
import {
  AttendanceChatMessage,
  AttendanceChatSendExtras,
  AttendanceClassDateSelectionPayload,
  AttendanceDateSelectionPayload,
  AttendanceStudentCandidate,
  AttendanceStudentReport,
} from './modes/attendance/attendanceWizardTypes'
import {useAttendanceSuggestionSamples} from './modes/attendance/useAttendanceSuggestionSamples'

/**
 * Keep under Metronic's fixed header (`$app-header-fixed-z-index`, 100) so header
 * dropdowns stay clickable while the widget is open; above the sticky toolbar (99)
 * by DOM order.
 */
const WIDGET_Z_INDEX = 99

/**
 * Bottom edge of the fixed app header in viewport pixels — the fullscreen panel's top.
 * Uses the outer `#kt_app_header` rect rather than the inner wrapper's height so the
 * support-staff alert bar that pushes the header down is accounted for.
 */
function measureHeaderBottom(): number {
  const header = document.getElementById('kt_app_header')
  if (!header) return 0
  return Math.max(0, Math.round(header.getBoundingClientRect().bottom))
}

interface ChatbotWidgetProps {
  isOpen: boolean
  isFullscreen?: boolean
  messages: AttendanceChatMessage[]
  isTyping: boolean
  theme: 'light' | 'dark'
  onClose: () => void
  onToggleFullscreen?: () => void
  onSendMessage: (text: string) => void
  onToggleTheme: () => void
  currentMenuItems?: MenuItem[]
  onMenuSelect?: (item: MenuItem) => void
  onAttendanceMenuOption?: (item: MenuItem) => void
  menuStack?: MenuItem[]
  onMenuReset?: () => void
  isInMenu?: boolean
  menuState: {
    isMenuOpen: boolean
    isSubmenuOpen: boolean
    isConversationStarted: boolean
  }
  onMenuStateChange: (
    newState: Partial<{
      isMenuOpen: boolean
      isSubmenuOpen: boolean
      isConversationStarted: boolean
    }>
  ) => void
  showMinimizedMenu?: boolean
  onOpenMinimizedMenu?: () => void
  onMenuGoBack?: () => void
  restoreModalOpen?: boolean
  onCloseRestoreModal?: () => void
  onRestoreContinue?: () => void
  onRestoreNewChat?: () => void
  onNewChat?: () => void
  onDateSelect?: (date: Date) => void
  onDateRangeSelect?: (start: Date, end: Date) => void
  datePickerMode?: 'single' | 'range'
  onSkipQuestion?: () => void
  showDatePicker?: boolean
  showAttendanceDateRange?: boolean
  showAttendanceDatePicker?: boolean
  attendanceDateSelection?: AttendanceDateSelectionPayload
  attendanceClassDateSelection?: AttendanceClassDateSelectionPayload
  onAttendanceDateConfirm?: (selection: {
    dateFrom: string
    dateTo: string
    selectedDates?: string[]
    selectedMonthKeys?: string[]
    monthLabels?: string[]
  }) => void
  isDateQuestionRequired?: boolean
  showOptionChips?: boolean
  optionChipsOptions?: Array<string | {label?: string; id?: string; value?: string; name?: string}>
  onOptionSelect?: (value: string, extras?: AttendanceChatSendExtras) => void
  selectedOptionValue?: string | null
  optionChipsLayout?: 'horizontal' | 'vertical'
  showSchoolSelect?: boolean
  schoolSelectOptions?: SelectOption[]
  schoolSelectPlaceholder?: string
  schoolSelectEmptyMessage?: string
  onSchoolSelect?: (value: string) => void
  showCountrySelect?: boolean
  onCountrySelect?: (country: string) => void
  selectedCountry?: string | null
  showBloodGroupSelect?: boolean
  onBloodGroupSelect?: (bloodGroup: string) => void
  selectedBloodGroup?: string | null
  inputType?: string
  phoneOptions?: any[]
  showSkipButton?: boolean
  skipButtonMessageId?: string
  showFileUpload?: boolean
  onFileUpload?: (file: File, preview: string) => void
  currentQuestionIndex?: number | null
  isFullWidth?: boolean
  isMinimized?: boolean
  onMinimize?: () => void
  onHide?: () => void
  getVoiceConversationHistory?: () => Array<{query: string; answer: string}>
  onAppendVoiceConversationPair?: (pair: {query: string; answer: string}) => void
  allowVoice?: boolean
  chatbotMode?: ChatbotMode
  getMenuSelectedPath?: () => string
  widgetTitleMessageId?: string
  hideMenu?: boolean
  datePickerMinDate?: Date
  datePickerMaxDate?: Date
  disableChatInput?: boolean
  showAttendanceStudentMultiSelect?: boolean
  attendanceStudentCandidates?: AttendanceStudentCandidate[]
  onConfirmAttendanceStudents?: (selected: AttendanceStudentCandidate[]) => void
  showAttendanceClassScopePicker?: boolean
  attendanceClassScopeCandidates?: AttendanceStudentCandidate[]
  attendanceClassScopeMaxSelection?: number
  attendanceClassScopeHintMessageId?: string
  onConfirmAttendanceClassScope?: (selected: AttendanceStudentCandidate[]) => void
  showAttendanceClassDisambiguation?: boolean
  attendanceClassDisambiguationCandidates?: AttendanceStudentCandidate[]
  onConfirmAttendanceClassDisambiguation?: (selected: AttendanceStudentCandidate[]) => void
  showAttendanceSchoolMultiSelect?: boolean
  attendanceSchoolMultiOptions?: SelectOption[]
  onConfirmAttendanceSchoolMulti?: (result: {
    values: string[]
    labels: string[]
    selectAll: boolean
  }) => void
  showAttendanceMonthYear?: boolean
  onAttendanceMonthYearSelect?: (month: number, year: number) => void
  onAttendanceChatInputChange?: (text: string) => void
  onSchoolSelectSearchChange?: (term: string) => void
  onChangeAttendanceDates?: (reports: AttendanceStudentReport[]) => void
  /** Keep widget mounted while closed so opening does not remount the full tree. */
  keepMounted?: boolean
  /**
   * Welcome-message chips. Attendance mode builds its own live examples internally and
   * appends these after them; other modes show exactly these.
   */
  welcomeSuggestions?: AttendanceInputSuggestion[]
  /** Renders a "Create a support ticket" action under every knowledge-base answer. */
  onCreateTicket?: () => void
  /**
   * What the assistant can do, listed as chips on the welcome message (§8 open decision 3:
   * first-open chips, not tabs). Picking one steers that turn via `options.capabilityId`.
   */
  welcomeCapabilities?: AssistantCapability[]
  /** The surface the conversation on screen belongs to. Filters the clarification chips shown and
   *  resets the input when it changes. */
  surface?: ChatSurface
  /** Hides the live attendance example chips and follow-up pills (admission pages). */
  hideAttendanceAffordances?: boolean
  /** The capability the backend attributed the latest turn to (`attendance.data`,
   *  `support.manual`, …). The attendance follow-up pills show only after an attendance answer. */
  lastAnswerCapability?: string | null
  /** Overrides the header subtitle the mode would pick. */
  subtitleMessageId?: string
}

export interface ChatbotWidgetHandle {
  pauseVoiceCall: () => void
  resumeVoiceCall: () => void
  isPaused: boolean
}

/** What a clicked slot chip puts in the input for the reader to finish (backend slot ids, see
 *  router/clarify.js SLOT_QUESTIONS). */
const SLOT_INPUT_STARTERS: Record<string, string> = {
  group: 'for class ',
  period: 'from ',
  person: 'for ',
}

/** The message as it is shown on this surface — same object unless a clarification option is
 *  dropped, so the stored clarification (echoed on the next turn) is never touched. */
function messageForSurface(
  message: AttendanceChatMessage,
  surface: ChatSurface
): AttendanceChatMessage {
  if (!message.clarification) return message
  const shown = filterClarificationForSurface(message.clarification, surface)
  return shown === message.clarification ? message : {...message, clarification: shown}
}

const ChatbotWidget = forwardRef<ChatbotWidgetHandle, ChatbotWidgetProps>(
  (
    {
      isOpen,
      isFullscreen = false,
      messages,
      isTyping,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      theme: _theme,
      onClose,
      onToggleFullscreen,
      onSendMessage,
      currentMenuItems,
      onMenuSelect,
      onAttendanceMenuOption,
      menuStack,
      menuState,
      onMenuStateChange,
      showMinimizedMenu,
      onOpenMinimizedMenu,
      onMenuGoBack,
      restoreModalOpen,
      onCloseRestoreModal,
      onRestoreContinue,
      onRestoreNewChat,
      onNewChat,
      onDateSelect,
      onDateRangeSelect,
      datePickerMode = 'single',
      onSkipQuestion,
      showDatePicker = false,
      showAttendanceDateRange = false,
      showAttendanceDatePicker = false,
      attendanceDateSelection,
      attendanceClassDateSelection,
      onAttendanceDateConfirm,
      isDateQuestionRequired = false,
      showOptionChips = false,
      optionChipsOptions = [],
      onOptionSelect,
      selectedOptionValue = null,
      optionChipsLayout = 'horizontal',
      showSchoolSelect = false,
      schoolSelectOptions = [],
      schoolSelectPlaceholder = '',
      schoolSelectEmptyMessage = '',
      onSchoolSelect,
      showCountrySelect = false,
      onCountrySelect,
      selectedCountry = null,
      showBloodGroupSelect = false,
      onBloodGroupSelect,
      selectedBloodGroup = null,
      inputType,
      phoneOptions,
      showSkipButton = false,
      skipButtonMessageId = 'CHATBOT.WIDGET_SKIP_QUESTION',
      showFileUpload = false,
      onFileUpload,
      currentQuestionIndex = null,
      isFullWidth = false,
      getVoiceConversationHistory,
      onAppendVoiceConversationPair,
      allowVoice = true,
      chatbotMode = 'general',
      getMenuSelectedPath,
      widgetTitleMessageId = 'CHATBOT.WIDGET_TITLE',
      hideMenu = false,
      datePickerMinDate,
      datePickerMaxDate,
      disableChatInput = false,
      showAttendanceStudentMultiSelect = false,
      attendanceStudentCandidates = [],
      onConfirmAttendanceStudents,
      showAttendanceClassScopePicker = false,
      attendanceClassScopeCandidates = [],
      attendanceClassScopeMaxSelection = 1,
      attendanceClassScopeHintMessageId = 'CHATBOT.ATTENDANCE.CLASS_SCOPE_SELECT_HINT',
      onConfirmAttendanceClassScope,
      showAttendanceClassDisambiguation = false,
      attendanceClassDisambiguationCandidates = [],
      onConfirmAttendanceClassDisambiguation,
      showAttendanceSchoolMultiSelect = false,
      attendanceSchoolMultiOptions = [],
      onConfirmAttendanceSchoolMulti,
      showAttendanceMonthYear = false,
      onAttendanceMonthYearSelect,
      onAttendanceChatInputChange,
      onSchoolSelectSearchChange,
      onChangeAttendanceDates,
      keepMounted = false,
      welcomeSuggestions,
      onCreateTicket,
      welcomeCapabilities,
      surface = 'general',
      hideAttendanceAffordances = false,
      lastAnswerCapability = null,
      subtitleMessageId,
    },
    ref
  ) => {
    const intl = useIntl()
    const resolvedTheme = useChatbotResolvedTheme()

    // Examples are built from this school's own active grades/subjects, so they never suggest a
    // grade the school does not have.
    // Starting over throws away the conversation, so it always goes through a confirm step.
    const [newChatConfirmOpen, setNewChatConfirmOpen] = useState(false)

    // Looked up while the widget is MOUNTED, not only once it is open. The names take three
    // round trips to settle, so starting them at open meant the examples rendered generic and
    // rewrote themselves in front of the reader; started early (and remembered between opens),
    // the first paint is already the school's own grade. `isOpen` only decides whether to wait
    // for the page to go quiet first.
    const attendanceSuggestionSamples = useAttendanceSuggestionSamples(
      chatbotMode === 'attendance',
      isOpen
    )

    const attendanceWelcomeSuggestions = useMemo(() => {
      if (chatbotMode !== 'attendance') return []
      return getWelcomeAttendanceSuggestions(
        (id, values) => intl.formatMessage({id}, values),
        attendanceSuggestionSamples
      )
    }, [attendanceSuggestionSamples, chatbotMode, intl])

    // Attendance builds its chips from live school data and the caller's chips (e.g. one
    // how-to question) follow them; other modes show only what the caller passes in.
    const effectiveWelcomeSuggestions = useMemo(() => {
      const passed = welcomeSuggestions ?? []
      if (chatbotMode === 'attendance' && !hideAttendanceAffordances) {
        return [...attendanceWelcomeSuggestions, ...passed]
      }
      return passed
    }, [attendanceWelcomeSuggestions, chatbotMode, hideAttendanceAffordances, welcomeSuggestions])

    /** True while the conversation is still on its welcome message — nothing asked yet. */
    const isFreshWelcome = useMemo(() => {
      if (isTyping) return false
      if (messages.some((m) => m.sender === 'user')) return false
      return messages.some((m) => m.id === 'welcome' && m.sender === 'bot')
    }, [isTyping, messages])

    const showWelcomeSuggestions = effectiveWelcomeSuggestions.length > 0 && isFreshWelcome
    const showWelcomeCapabilities = (welcomeCapabilities?.length ?? 0) > 0 && isFreshWelcome

    const attendanceAssistiveUiActive = useMemo(() => {
      if (chatbotMode !== 'attendance') return false
      return isAttendanceAssistiveUiActive({
        showAttendanceDateRange,
        showAttendanceDatePicker,
        hasDateSelectionPayload: Boolean(attendanceDateSelection || attendanceClassDateSelection),
        showSchoolSelect,
        showSchoolMultiSelect: showAttendanceSchoolMultiSelect,
        schoolMultiSelectOptionCount: attendanceSchoolMultiOptions.length,
        showMonthYearInput: showAttendanceMonthYear,
        showSkipButton,
        showStudentMultiSelect: showAttendanceStudentMultiSelect,
        studentCandidateCount: attendanceStudentCandidates.length,
        showClassScopePicker: showAttendanceClassScopePicker,
        showClassDisambiguation: showAttendanceClassDisambiguation,
        showOptionChips,
        optionChipsCount: optionChipsOptions.length,
      })
    }, [
      attendanceClassDateSelection,
      attendanceDateSelection,
      attendanceSchoolMultiOptions.length,
      attendanceStudentCandidates.length,
      chatbotMode,
      optionChipsOptions.length,
      showAttendanceClassDisambiguation,
      showAttendanceClassScopePicker,
      showAttendanceDatePicker,
      showAttendanceDateRange,
      showAttendanceMonthYear,
      showAttendanceSchoolMultiSelect,
      showAttendanceStudentMultiSelect,
      showOptionChips,
      showSchoolSelect,
      showSkipButton,
    ])

    const showAttendanceInputPills = useMemo(() => {
      if (chatbotMode !== 'attendance' || hideAttendanceAffordances) return false
      if (isTyping || attendanceAssistiveUiActive || showWelcomeSuggestions) return false
      if (!messages.some((m) => m.sender === 'user')) return false
      const lastBotMessage = [...messages].reverse().find((m) => m.sender === 'bot')
      return attendancePillsFitLastTurn(lastBotMessage, lastAnswerCapability)
    }, [
      attendanceAssistiveUiActive,
      chatbotMode,
      hideAttendanceAffordances,
      isTyping,
      lastAnswerCapability,
      messages,
      showWelcomeSuggestions,
    ])

    const messagesEndRef = useRef<HTMLDivElement>(null)
    const messagesContainerRef = useRef<HTMLDivElement>(null)
    const prevIsOpenRef = useRef(isOpen)
    const stickToBottomRef = useRef(true)

    const scrollMessagesToBottom = useCallback((instant = false) => {
      const container = messagesContainerRef.current
      if (!container) {
        messagesEndRef.current?.scrollIntoView({
          behavior: instant ? 'auto' : 'smooth',
          block: 'end',
        })
        return
      }
      const top = container.scrollHeight - container.clientHeight
      container.scrollTo({
        top: top > 0 ? top : 0,
        behavior: instant ? 'auto' : 'smooth',
      })
    }, [])

    const scheduleScrollToBottom = useCallback(
      (instant = false) => {
        scrollMessagesToBottom(instant)
        requestAnimationFrame(() => {
          scrollMessagesToBottom(instant)
          requestAnimationFrame(() => scrollMessagesToBottom(instant))
        })
      },
      [scrollMessagesToBottom]
    )

    const forceScrollToBottom = useCallback(() => {
      stickToBottomRef.current = true
      scheduleScrollToBottom(false)
      window.setTimeout(() => scheduleScrollToBottom(false), 150)
      window.setTimeout(() => scheduleScrollToBottom(false), 400)
    }, [scheduleScrollToBottom])

    const handleAttendanceSendMessage = useCallback(
      (text: string) => {
        forceScrollToBottom()
        onSendMessage(text)
      },
      [forceScrollToBottom, onSendMessage]
    )

    const handleAttendanceOptionSelect = useCallback(
      (value: string, extras?: AttendanceChatSendExtras) => {
        forceScrollToBottom()
        onOptionSelect?.(value, extras)
      },
      [forceScrollToBottom, onOptionSelect]
    )

    const attendanceDisambiguationScrollKey = useMemo(() => {
      if (chatbotMode !== 'attendance') return ''
      const lastBot = [...messages].reverse().find((m) => m.sender === 'bot')
      const v2Payload = (
        lastBot as {
          v2Payload?: {kind?: string; disambiguation?: {field?: string; options?: unknown[]}}
        }
      )?.v2Payload
      if (v2Payload?.kind !== 'disambiguation' || !v2Payload.disambiguation) return ''
      return `disambiguation-${v2Payload.disambiguation.field ?? 'unknown'}-${v2Payload.disambiguation.options?.length ?? 0}`
    }, [chatbotMode, messages])

    const {
      status: callStatus,
      transcriptionEnabled,
      isMuted: callMuted,
      error: callError,
      transcript,
      interimUserTranscript,
      micLevel,
      awaitingResponse,
      isCallSupported,
      startCallFlow,
      chooseTranscription,
      chooseVoice,
      voices,
      selectedVoice,
      endCall,
      returnToIdle,
      toggleMute,
      retryCall,
      pauseCall,
      resumeCall,
      isPaused,
    } = useVoiceCallState({
      getConversationHistory: getVoiceConversationHistory,
      onAppendConversationPair: onAppendVoiceConversationPair,
      chatbotMode,
      getMenuSelectedPath,
      onInsertChatEvent: (ev: any) => {
        try {
          if (ev && ev.type === 'call_summary') {
            dispatch(
              addUserMessage({
                ...ev,
                sender: 'user',
                timestamp: ev.timestamp || new Date(),
              })
            )
          } else if (ev && ev.sender === 'user') {
            dispatch(addUserMessage(ev))
          } else {
            dispatch(addBotMessage(ev))
          }
        } catch (e) {
          calculateErrorMessage(e)
        }
      },
      onWalkthroughResume: () => {
        try {
          stickToBottomRef.current = true
          scheduleScrollToBottom(false)
          if (!isFullscreen && onToggleFullscreen) onToggleFullscreen()
        } catch (e) {
          calculateErrorMessage(e)
        }
      },
    })

    useImperativeHandle(
      ref,
      () => ({
        pauseVoiceCall: pauseCall,
        resumeVoiceCall: resumeCall,
        isPaused,
      }),
      [pauseCall, resumeCall, isPaused]
    )

    const dispatch = useDispatch()

    const showTranscriptionModal = callStatus === 'choosing_transcription'
    const showVoiceOverlay =
      callStatus === 'connecting' ||
      callStatus === 'listening' ||
      callStatus === 'responding' ||
      callStatus === 'muted' ||
      callStatus === 'walkthrough_paused' ||
      callStatus === 'ended' ||
      callStatus === 'error'
    const isCallActive =
      callStatus === 'connecting' ||
      callStatus === 'listening' ||
      callStatus === 'responding' ||
      callStatus === 'muted' ||
      callStatus === 'walkthrough_paused'
    const voiceCallDisabled =
      !isCallSupported || (callStatus !== 'idle' && callStatus !== 'walkthrough_paused')

    const [shouldRender, setShouldRender] = React.useState(isOpen || keepMounted)

    const [selectedMenuItem, setSelectedMenuItem] = useState<MenuItem | null>(null)
    const [inputText, setInputText] = useState<string>('')
    const inputRef = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>
    const prevIsOpenForInputResetRef = useRef(isOpen)

    const handleAttendanceSuggestionSelect = useCallback(
      (text: string) => {
        setInputText(text)
        onAttendanceChatInputChange?.(text)
        inputRef.current?.focus()
      },
      [onAttendanceChatInputChange]
    )

    /**
     * A clarification option was picked.
     *
     * The label goes back EXACTLY as the backend offered it — that is how the backend matches
     * the reply to its options, for every kind of clarification (capability, slot, menu). An
     * option carrying a `capabilityId` also sends that id, which the backend revalidates against
     * its registry and re-clamps to the caller's permissions. The pending clarification itself
     * is echoed by `useAssistantChat`, so the reply is bound to the question that asked it and
     * goes to the same board that returned it.
     */
    const handleClarificationSelect = useCallback(
      (option: AssistantClarificationOption) => {
        const label = option.label?.trim()
        if (!label) {
          inputRef.current?.focus()
          return
        }
        // A slot chip ("which class, section or grade", "which dates") names what is missing; it
        // is not an answer. Sending it asked the bot its own question back and ran the original
        // query unscoped ("No attendance records were found…"). Start the reply in the input
        // instead — the backend binds whatever is typed to the pending slot.
        if (option.slot) {
          const starter = SLOT_INPUT_STARTERS[option.slot] ?? ''
          setInputText(starter)
          onAttendanceChatInputChange?.(starter)
          inputRef.current?.focus()
          return
        }
        forceScrollToBottom()
        onOptionSelect?.(
          label,
          option.capabilityId ? {capabilityId: option.capabilityId} : undefined
        )
      },
      [forceScrollToBottom, onAttendanceChatInputChange, onOptionSelect]
    )

    // A widening offer starts the next question in the input rather than sending it, like the
    // welcome suggestions and slot chips — the user reviews or edits it, then sends.
    const handleWidenSuggestionSelect = useCallback(
      (suggestion: AssistantSuggestion) => {
        handleAttendanceSuggestionSelect(suggestion.query?.trim() || suggestion.label)
      },
      [handleAttendanceSuggestionSelect]
    )

    const handleCapabilitySelect = useCallback(
      (capability: AssistantCapability) => {
        forceScrollToBottom()
        onOptionSelect?.(capability.label, {capabilityId: capability.id})
      },
      [forceScrollToBottom, onOptionSelect]
    )

    const [showVoiceTip, setShowVoiceTip] = useState(false)

    useEffect(() => {
      const justOpened = isOpen && !prevIsOpenForInputResetRef.current
      prevIsOpenForInputResetRef.current = isOpen
      if (justOpened && chatbotMode === 'attendance') {
        setInputText('')
        try {
          if (!localStorage.getItem('chatbot-voice-tip-seen')) {
            setShowVoiceTip(true)
            localStorage.setItem('chatbot-voice-tip-seen', 'true')
          }
        } catch (e) {
          calculateErrorMessage(e)
        }
      }
    }, [chatbotMode, isOpen])

    useEffect(() => {
      if (!showVoiceTip) return
      const timer = setTimeout(() => setShowVoiceTip(false), 6000)
      return () => clearTimeout(timer)
    }, [showVoiceTip])

    // Navigating onto (or off) an admission page swaps the conversation on screen. A half-typed
    // question or an open "start over?" confirm belonged to the one that just left.
    const prevSurfaceRef = useRef(surface)
    useEffect(() => {
      if (prevSurfaceRef.current === surface) return
      prevSurfaceRef.current = surface
      setInputText('')
      setNewChatConfirmOpen(false)
    }, [surface])
    const {isMenuOpen, isConversationStarted} = menuState
    const processedMenuItems = React.useMemo(() => {
      return currentMenuItems || []
    }, [currentMenuItems])

    const [showMenu, setShowMenu] = React.useState(true)
    const shouldShowMenu = React.useMemo(() => {
      return isMenuOpen && !isConversationStarted
    }, [isMenuOpen, isConversationStarted])

    const showAttendanceOptionsAtRoot = showsAttendanceSubmenuAtRoot(chatbotMode)
    const showRootSuggestionOptions = showAttendanceOptionsAtRoot && (menuStack?.length ?? 0) === 0

    // Measured lazily on mount, not started at 0: during a mode switch the
    // incoming widget mounts while the panel may already be fullscreen, and a
    // 0/0 first paint put it at the viewport's top-left — the container's
    // `transition: all 400ms` then dragged the whole modal across the screen.
    const [headerHeight, setHeaderHeight] = useState(() => measureHeaderBottom())
    const [sidebarWidth, setSidebarWidth] = useState(
      () => (document.querySelector('.app-sidebar') as HTMLElement | null)?.offsetWidth || 0
    )

    const calculateLayoutDimensions = useCallback(() => {
      try {
        const sidebar = document.querySelector('.app-sidebar') as HTMLElement

        const calculatedHeaderHeight = measureHeaderBottom()
        const calculatedSidebarWidth = sidebar?.offsetWidth || 0

        setHeaderHeight(calculatedHeaderHeight)
        setSidebarWidth(calculatedSidebarWidth)
      } catch (e) {
        calculateErrorMessage(e)
      }
    }, [])

    useEffect(() => {
      setInputText('')
    }, [inputType])

    useEffect(() => {
      setShowMenu(shouldShowMenu)
    }, [shouldShowMenu])

    useEffect(() => {
      const container = messagesContainerRef.current
      if (!container) return

      const onScroll = () => {
        const distanceFromBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight
        stickToBottomRef.current = distanceFromBottom < 96
      }

      container.addEventListener('scroll', onScroll, {passive: true})
      return () => container.removeEventListener('scroll', onScroll)
    }, [shouldRender, messages.length])

    useEffect(() => {
      if (!isOpen || !shouldRender) return

      const container = messagesContainerRef.current
      if (!container) return

      const observer = new ResizeObserver(() => {
        if (stickToBottomRef.current) {
          scrollMessagesToBottom(true)
        }
      })
      observer.observe(container)

      return () => observer.disconnect()
    }, [isOpen, shouldRender, scrollMessagesToBottom])

    useEffect(() => {
      const justOpened = isOpen && !prevIsOpenRef.current
      prevIsOpenRef.current = isOpen

      if (!isOpen || !shouldRender) return

      if (justOpened) {
        stickToBottomRef.current = true
      }

      if (!stickToBottomRef.current && !justOpened) return

      scheduleScrollToBottom(justOpened)
      const timer = window.setTimeout(() => scheduleScrollToBottom(justOpened), 150)

      return () => window.clearTimeout(timer)
    }, [
      messages,
      isTyping,
      isOpen,
      shouldRender,
      scheduleScrollToBottom,
      attendanceDisambiguationScrollKey,
      showAttendanceDatePicker,
      showAttendanceDateRange,
      showAttendanceStudentMultiSelect,
      showAttendanceSchoolMultiSelect,
      showAttendanceMonthYear,
      showSchoolSelect,
      showSkipButton,
      disableChatInput,
      attendanceStudentCandidates.length,
      attendanceSchoolMultiOptions.length,
    ])

    useEffect(() => {
      if (
        chatbotMode !== 'attendance' ||
        !attendanceDisambiguationScrollKey ||
        !isOpen ||
        !shouldRender
      ) {
        return
      }
      forceScrollToBottom()
    }, [attendanceDisambiguationScrollKey, chatbotMode, forceScrollToBottom, isOpen, shouldRender])

    useEffect(() => {
      if (chatbotMode !== 'attendance' || !isOpen || !shouldRender) return

      const container = messagesContainerRef.current
      if (!container) return

      let rafId: number | null = null
      const observer = new MutationObserver(() => {
        if (!stickToBottomRef.current) return
        if (rafId != null) cancelAnimationFrame(rafId)
        rafId = requestAnimationFrame(() => scrollMessagesToBottom(true))
      })

      observer.observe(container, {childList: true, subtree: true})
      return () => {
        observer.disconnect()
        if (rafId != null) cancelAnimationFrame(rafId)
      }
    }, [chatbotMode, isOpen, shouldRender, scrollMessagesToBottom])

    useEffect(() => {
      if (isFullscreen) {
        calculateLayoutDimensions()
      }
    }, [isFullscreen, calculateLayoutDimensions])

    useEffect(() => {
      if (!isFullscreen) return

      calculateLayoutDimensions()

      const bodyElement = document.body
      const sidebarElement = document.querySelector('.app-sidebar')

      const observer = new MutationObserver(() => {
        setTimeout(() => {
          calculateLayoutDimensions()
        }, 50)
      })

      observer.observe(bodyElement, {
        attributes: true,
        attributeFilter: ['data-kt-app-sidebar-minimize', 'class'],
      })
      // The support-staff alert pushes the header down through a style variable on <html>.
      observer.observe(document.documentElement, {attributes: true, attributeFilter: ['style']})

      if (sidebarElement) {
        observer.observe(sidebarElement, {
          attributes: true,
          attributeFilter: ['class', 'style'],
        })
      }

      const handleResize = () => {
        calculateLayoutDimensions()
      }
      window.addEventListener('resize', handleResize)

      return () => {
        observer.disconnect()
        window.removeEventListener('resize', handleResize)
      }
    }, [calculateLayoutDimensions, isFullscreen])

    useEffect(() => {
      if (selectedMenuItem) {
        const timer = setTimeout(() => {
          setSelectedMenuItem(null)
        }, 1500)
        return () => clearTimeout(timer)
      }
    }, [selectedMenuItem])

    useEffect(() => {
      if (keepMounted) {
        setShouldRender(true)
        return
      }

      if (isOpen) {
        setShouldRender(true)
      } else {
        const timeout = setTimeout(() => setShouldRender(false), 250)
        return () => clearTimeout(timeout)
      }
    }, [isOpen, keepMounted])

    useEffect(() => {
      if (showVoiceOverlay && !isFullscreen && onToggleFullscreen) {
        onToggleFullscreen()
      }
    }, [showVoiceOverlay, isFullscreen, onToggleFullscreen])

    useEffect(() => {
      try {
        if (showVoiceOverlay) {
          document.body.classList.add('voice-overlay-active')
        } else {
          document.body.classList.remove('voice-overlay-active')
        }
      } catch (e) {
        calculateErrorMessage(e)
      }
      return () => {
        try {
          document.body.classList.remove('voice-overlay-active')
        } catch (e) {
          calculateErrorMessage(e)
        }
      }
    }, [showVoiceOverlay])

    if (!shouldRender) return null

    const animationClass = isOpen
      ? 'chatbot-widget-container--enter'
      : 'chatbot-widget-container--exit'

    const isAttendanceMode = chatbotMode === 'attendance'
    const isFaqMode = chatbotMode === 'faq'
    // Both boards present as the one "Versi AI Assistant"; only the generic bot keeps its own name.
    const isAssistant = isAttendanceMode || isFaqMode

    const headerBrandMessageId = isAssistant
      ? 'CHATBOT.WIDGET_BRAND_ASSISTANT'
      : 'CHATBOT.WIDGET_BRAND'

    const headerSubtitleMessageId = subtitleMessageId
      ? subtitleMessageId
      : isAssistant
        ? 'CHATBOT.WIDGET_SUBTITLE_ASSISTANT'
        : widgetTitleMessageId === 'CHATBOT.WIDGET_TITLE'
          ? 'CHATBOT.WIDGET_SUBTITLE_SUPPORT'
          : 'CHATBOT.WIDGET_SUBTITLE_DEFAULT'

    return (
      <>
        <div
          className={`${isFullWidth ? 'position-relative chatbot-widget-container--fullwidth' : 'position-fixed'} ${!isFullWidth && !isFullscreen ? 'end-0 chatbot-widget-container--floating' : ''} d-flex flex-column ${isFullscreen ? 'chatbot-widget-container--fullscreen' : ''} chatbot-widget-container ${animationClass}`}
          data-chatbot-theme={resolvedTheme}
          style={{
            position: isFullWidth ? 'relative' : 'fixed',
            ...(isFullWidth
              ? {width: '100%', height: '100%'}
              : isFullscreen
                ? {
                    width: 'auto',
                    height: `calc(100dvh - ${headerHeight}px)`,
                    maxHeight: '100%',
                    top: `${headerHeight}px`,
                    left: `${sidebarWidth}px`,
                    right: '0',
                    marginRight: '0',
                  }
                : {}),
            // Below the fixed app header (z-index 100) so the navbar's own dropdowns —
            // which live inside the header's stacking context — open above the widget in
            // both floating and fullscreen views.
            zIndex: isFullWidth ? 1 : WIDGET_Z_INDEX,
            transition: 'all 400ms ease-in-out',
            overflow: 'hidden',
          }}
        >
          {!showVoiceOverlay && (
            <>
              {restoreModalOpen && (
                <PromptModal
                  open={!!restoreModalOpen}
                  onClose={onCloseRestoreModal || (() => {})}
                  placement='center'
                  centerOnParent={true}
                  width={380}
                  title={intl.formatMessage({
                    id: 'CHATBOT.RESTORE_TITLE',
                  })}
                  body={intl.formatMessage({
                    id: 'CHATBOT.RESTORE_BODY',
                  })}
                  primaryLabel={intl.formatMessage({
                    id: 'CHATBOT.RESTORE_CONTINUE',
                  })}
                  onPrimary={onRestoreContinue}
                  secondaryLabel={intl.formatMessage({
                    id: 'CHATBOT.RESTORE_NEW',
                  })}
                  onSecondary={onRestoreNewChat}
                  theme={resolvedTheme}
                />
              )}
              <ChatbotHeader
                theme={resolvedTheme}
                avatar={isAssistant ? <AttendanceBotAvatar /> : undefined}
                brandMessageId={headerBrandMessageId}
                subtitleMessageId={headerSubtitleMessageId}
                showBetaBadge={isAssistant}
                showStatus={!isAssistant}
                allowVoice={allowVoice}
                voiceCallDisabled={voiceCallDisabled}
                onStartCall={startCallFlow}
                showFullscreenToggle={!isFullWidth}
                isFullscreen={isFullscreen}
                onToggleFullscreen={onToggleFullscreen}
                onClose={onClose}
                onNewChat={onNewChat ? () => setNewChatConfirmOpen(true) : undefined}
              />

              {newChatConfirmOpen && (
                <PromptModal
                  open={newChatConfirmOpen}
                  onClose={() => setNewChatConfirmOpen(false)}
                  placement='center'
                  centerOnParent={true}
                  width={320}
                  title={intl.formatMessage({id: 'CHATBOT.NEW_CHAT_CONFIRM_TITLE'})}
                  body={intl.formatMessage({id: 'CHATBOT.NEW_CHAT_CONFIRM_BODY'})}
                  primaryLabel={intl.formatMessage({id: 'CHATBOT.NEW_CHAT_CONFIRM_YES'})}
                  onPrimary={() => {
                    setNewChatConfirmOpen(false)
                    onNewChat?.()
                  }}
                  secondaryLabel={intl.formatMessage({id: 'CHATBOT.NEW_CHAT_CONFIRM_CANCEL'})}
                  onSecondary={() => setNewChatConfirmOpen(false)}
                  theme={resolvedTheme}
                />
              )}

              {currentQuestionIndex != null && (
                <div
                  style={{
                    padding: '12px 16px',
                    backgroundColor: 'var(--bs-app-bg-color)',
                    borderBottom:
                      resolvedTheme === 'dark' ? '1px solid #374151' : '1px solid #e5e7eb',
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '8px',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: resolvedTheme === 'dark' ? '#e5e7eb' : '#6b7280',
                      }}
                    >
                      Question {Math.min(currentQuestionIndex + 1, 32)} of 32
                    </span>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: resolvedTheme === 'dark' ? '#60a5fa' : '#2563eb',
                      }}
                    >
                      {Math.min(Math.round(((currentQuestionIndex + 1) / 32) * 100), 100)}%
                    </span>
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: '6px',
                      backgroundColor: resolvedTheme === 'dark' ? '#374151' : '#e5e7eb',
                      borderRadius: '3px',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.min(((currentQuestionIndex + 1) / 32) * 100, 100)}%`,
                        height: '100%',
                        backgroundColor: resolvedTheme === 'dark' ? '#60a5fa' : '#2563eb',
                        borderRadius: '3px',
                        transition: 'width 0.3s ease',
                        boxShadow:
                          resolvedTheme === 'dark' ? '0 0 8px rgba(96, 165, 250, 0.3)' : 'none',
                      }}
                    />
                  </div>
                </div>
              )}

              <div
                ref={messagesContainerRef}
                className='flex-grow-1 overflow-auto p-3 chatbot-widget-messages'
                style={{
                  flex: '1 1 0',
                  minHeight: 0,
                  overflowY: messages.length > 0 || isTyping ? 'auto' : 'hidden',
                }}
              >
                {messages.length === 0 ? (
                  <EmptyState theme={resolvedTheme} isCompact={false} />
                ) : (
                  messages.map((message) => {
                    if (message && (message as any).type === 'call_summary') {
                      return (
                        <div key={message.id || (message as any).type}>
                          {showVoiceOverlay &&
                            createPortal(
                              <div
                                className='fixed inset-0'
                                style={{
                                  position: 'fixed',
                                  inset: 0,
                                  background: 'rgba(0,0,0,0.5)',
                                  zIndex: 99999,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <div
                                  style={{
                                    background: '#fff',
                                    color: '#000',
                                    padding: 24,
                                    borderRadius: 8,
                                    textAlign: 'center',
                                    minWidth: 280,
                                  }}
                                >
                                  <div style={{fontSize: 18, fontWeight: 700, marginBottom: 8}}>
                                    {'Please wait…'}
                                  </div>
                                  <div style={{fontSize: 14}}>Voice input paused</div>
                                </div>
                              </div>,
                              document.body
                            )}

                          <div className='d-flex justify-content-end mb-3'>
                            <div style={{maxWidth: '75%'}}>
                              <CallSummaryCard message={message} theme={resolvedTheme} />
                            </div>
                          </div>
                        </div>
                      )
                    }
                    return message.sender === 'user' ? (
                      <UserMessage
                        key={message.id}
                        message={message}
                        onCorrect={(text: string) => setInputText(text)}
                      />
                    ) : (
                      <BotMessage
                        key={message.id}
                        message={messageForSurface(message, surface)}
                        theme={resolvedTheme}
                        onOptionSelect={
                          chatbotMode === 'attendance' ? handleAttendanceOptionSelect : undefined
                        }
                        onChangeAttendanceDates={
                          chatbotMode === 'attendance' ? onChangeAttendanceDates : undefined
                        }
                        welcomeSuggestions={
                          message.id === 'welcome' && showWelcomeSuggestions
                            ? effectiveWelcomeSuggestions
                            : undefined
                        }
                        onWelcomeSuggestionSelect={handleAttendanceSuggestionSelect}
                        welcomeSuggestionsDisabled={isTyping || isCallActive}
                        onCreateTicket={onCreateTicket}
                        onClarificationSelect={
                          onOptionSelect ? handleClarificationSelect : undefined
                        }
                        onSuggestionSelect={
                          onOptionSelect ? handleWidenSuggestionSelect : undefined
                        }
                        chipsDisabled={isTyping || isCallActive}
                        capabilities={
                          message.id === 'welcome' && showWelcomeCapabilities
                            ? welcomeCapabilities
                            : undefined
                        }
                        onCapabilitySelect={onOptionSelect ? handleCapabilitySelect : undefined}
                      />
                    )
                  })
                )}

                {isTyping && <TypingIndicator theme={resolvedTheme} />}
                <div ref={messagesEndRef} />
              </div>

              <div className='p-3 border-top chatbot-widget-footer' style={{flexShrink: 0}}>
                {!hideMenu && showMenu && processedMenuItems && processedMenuItems.length > 0 && (
                  <div style={{marginBottom: 12}}>
                    {menuStack && menuStack.length === 0 && !showRootSuggestionOptions && (
                      <>
                        <div style={{marginBottom: 8}}>
                          <div
                            style={{
                              fontSize: '0.85rem',
                              fontWeight: 500,
                              color: 'var(--bs-text-muted)',
                            }}
                          >
                            {intl.formatMessage({
                              id: 'CHATBOT.WIDGET_MAIN_MENU',
                            })}
                          </div>
                        </div>
                        <MenuBubbles
                          options={processedMenuItems}
                          onOptionClick={(item: MenuItem) => {
                            setSelectedMenuItem(item)

                            if (onMenuSelect) {
                              onMenuSelect(item)
                            }
                          }}
                          loading={isTyping}
                        />
                      </>
                    )}

                    {(showRootSuggestionOptions || (menuStack && menuStack.length > 0)) && (
                      <>
                        {menuStack && menuStack.length > 0 && (
                          <div style={{marginBottom: 8}}>
                            <button
                              className='btn btn-sm btn-link text-primary'
                              onClick={onMenuGoBack}
                              style={{padding: '0 0 8px 0', textDecoration: 'none'}}
                            >
                              {intl.formatMessage({
                                id: 'CHATBOT.WIDGET_BACK',
                              })}
                            </button>
                          </div>
                        )}
                        {menuStack && menuStack.length > 0 && (
                          <div style={{marginBottom: 8}}>
                            <div
                              style={{
                                fontSize: '0.85rem',
                                fontWeight: 500,
                                color: 'var(--bs-text-muted)',
                              }}
                            >
                              {intl.formatMessage({
                                id: 'CHATBOT.WIDGET_SUGGESTIONS',
                              })}
                            </div>
                          </div>
                        )}
                        <SuggestionBubbles
                          options={processedMenuItems}
                          onOptionClick={(item: MenuItem) => {
                            setSelectedMenuItem(item)

                            if (showRootSuggestionOptions) {
                              if (onAttendanceMenuOption) {
                                onAttendanceMenuOption(item)
                              } else if (onMenuSelect) {
                                onMenuSelect(item)
                              }
                              return
                            }

                            if (menuStack && menuStack.length > 0) {
                              const text =
                                item.predefinedMessage ||
                                (item.labelId
                                  ? intl.formatMessage({
                                      id: item.labelId,
                                      defaultMessage: item.label,
                                    })
                                  : item.label)
                              setInputText(text)
                              setTimeout(() => {
                                try {
                                  inputRef.current?.focus()
                                  const el = inputRef.current
                                  if (el && typeof el.selectionStart === 'number') {
                                    const pos = el.value ? el.value.length : 0
                                    el.setSelectionRange(pos, pos)
                                  }
                                } catch (e) {
                                  calculateErrorMessage(e)
                                }
                              }, 0)
                            } else if (onMenuSelect) {
                              onMenuSelect(item)
                            }
                          }}
                          loading={isTyping}
                          selectedId={selectedMenuItem?.id}
                        />
                      </>
                    )}
                  </div>
                )}
                {!showMenu && showMinimizedMenu && (
                  <div
                    style={{
                      marginBottom: 12,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <button
                      type='button'
                      className='btn btn-sm btn-outline-primary'
                      onClick={() => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                        onOpenMinimizedMenu
                          ? onOpenMinimizedMenu()
                          : onMenuStateChange({
                              isMenuOpen: true,
                              isSubmenuOpen: false,
                              isConversationStarted: false,
                            })
                      }}
                      aria-label={intl.formatMessage({
                        id: 'CHATBOT.WIDGET_OPEN_MENU',
                      })}
                    >
                      {intl.formatMessage({
                        id: 'CHATBOT.WIDGET_OPEN_MENU',
                      })}
                    </button>
                  </div>
                )}
                {showSkipButton && onSkipQuestion && (
                  <div style={{marginBottom: '12px', display: 'flex', justifyContent: 'center'}}>
                    <button
                      className='btn btn-sm btn-info'
                      onClick={onSkipQuestion}
                      style={{fontSize: '0.875rem', padding: '0.35rem 0.75rem'}}
                    >
                      {intl.formatMessage({
                        id: skipButtonMessageId,
                      })}
                    </button>
                  </div>
                )}
                {chatbotMode === 'attendance' &&
                showAttendanceDatePicker &&
                onAttendanceDateConfirm &&
                (attendanceDateSelection || attendanceClassDateSelection) ? (
                  <AttendanceDatePicker
                    variant={attendanceDateSelection ? 'student' : 'class'}
                    studentPayload={attendanceDateSelection}
                    classPayload={attendanceClassDateSelection}
                    onConfirm={onAttendanceDateConfirm}
                    theme={resolvedTheme}
                    disabled={isTyping}
                  />
                ) : null}
                {chatbotMode === 'attendance' && showAttendanceDateRange && onDateRangeSelect ? (
                  <ChatbotDateRangeInput
                    onDateRangeSelect={onDateRangeSelect}
                    theme={resolvedTheme}
                    maxDate={datePickerMaxDate}
                  />
                ) : null}
                {chatbotMode === 'attendance' && showSchoolSelect && onSchoolSelect ? (
                  <AttendanceSchoolSelect
                    options={schoolSelectOptions}
                    onSelect={onSchoolSelect}
                    onSearchChange={onSchoolSelectSearchChange}
                    theme={resolvedTheme}
                    placeholder={schoolSelectPlaceholder}
                    emptyMessage={schoolSelectEmptyMessage}
                  />
                ) : null}
                {chatbotMode === 'attendance' &&
                showAttendanceSchoolMultiSelect &&
                onConfirmAttendanceSchoolMulti &&
                attendanceSchoolMultiOptions.length > 0 ? (
                  <AttendanceSchoolMultiSelect
                    options={attendanceSchoolMultiOptions}
                    onConfirm={onConfirmAttendanceSchoolMulti}
                    theme={resolvedTheme}
                    disabled={isTyping}
                  />
                ) : null}
                {chatbotMode === 'attendance' &&
                showAttendanceMonthYear &&
                onAttendanceMonthYearSelect ? (
                  <AttendanceMonthYearInput
                    onSelect={onAttendanceMonthYearSelect}
                    theme={resolvedTheme}
                    maxDate={datePickerMaxDate}
                  />
                ) : null}
                {chatbotMode === 'attendance' &&
                showAttendanceClassScopePicker &&
                onConfirmAttendanceClassScope &&
                attendanceClassScopeCandidates.length > 0 ? (
                  <AttendanceStudentMultiSelect
                    candidates={attendanceClassScopeCandidates}
                    onConfirm={onConfirmAttendanceClassScope}
                    theme={resolvedTheme}
                    disabled={isTyping}
                    maxSelection={attendanceClassScopeMaxSelection}
                    showItemId={false}
                    hintMessageId={attendanceClassScopeHintMessageId}
                    confirmMessageId='CHATBOT.ATTENDANCE.CONFIRM_CLASS_SCOPE'
                  />
                ) : null}
                {chatbotMode === 'attendance' &&
                showAttendanceClassDisambiguation &&
                onConfirmAttendanceClassDisambiguation &&
                attendanceClassDisambiguationCandidates.length > 0 ? (
                  <AttendanceStudentMultiSelect
                    candidates={attendanceClassDisambiguationCandidates}
                    onConfirm={onConfirmAttendanceClassDisambiguation}
                    theme={resolvedTheme}
                    disabled={isTyping}
                    maxSelection={1}
                    showItemId={false}
                    hintMessageId='CHATBOT.ATTENDANCE.CLASS_DISAMBIGUATION_SELECT_HINT'
                    confirmMessageId='CHATBOT.ATTENDANCE.CONFIRM_CLASS_SCOPE'
                  />
                ) : null}
                {chatbotMode === 'attendance' &&
                showAttendanceStudentMultiSelect &&
                onConfirmAttendanceStudents &&
                attendanceStudentCandidates.length > 0 ? (
                  <AttendanceStudentMultiSelect
                    candidates={attendanceStudentCandidates}
                    onConfirm={onConfirmAttendanceStudents}
                    theme={resolvedTheme}
                    disabled={isTyping}
                  />
                ) : null}
                {chatbotMode === 'attendance' ? (
                  <>
                    {showAttendanceInputPills && attendanceWelcomeSuggestions.length > 0 ? (
                      <AttendanceInputSuggestionPills
                        suggestions={attendanceWelcomeSuggestions}
                        onSelect={handleAttendanceSuggestionSelect}
                        disabled={isTyping || isCallActive}
                        theme={resolvedTheme}
                      />
                    ) : null}
                    <div className='position-relative'>
                      {showVoiceTip && (
                        <div
                          className='chatbot-voice-tip'
                          role='status'
                          onClick={() => setShowVoiceTip(false)}
                        >
                          {intl.formatMessage({id: 'CHATBOT.VOICE_TIP'})}
                        </div>
                      )}
                      <ChatInput
                        maxLength={2000}
                        isActive={isOpen}
                        onSend={(text: string) => {
                          onMenuStateChange({
                            isMenuOpen: false,
                            isSubmenuOpen: false,
                            isConversationStarted: true,
                          })
                          setShowMenu(false)
                          setSelectedMenuItem(null)
                          setInputText('')
                          handleAttendanceSendMessage(text)
                        }}
                        onFocus={() => {
                          try {
                            setShowMenu(true)
                            onMenuStateChange({
                              isMenuOpen: true,
                              isSubmenuOpen: false,
                              isConversationStarted: false,
                            })
                            setShowVoiceTip(false)
                          } catch (e) {
                            calculateErrorMessage(e)
                          }
                        }}
                        theme={resolvedTheme}
                        disabled={isTyping || isCallActive}
                        value={inputText}
                        onChangeValue={(next) => {
                          setInputText(next)
                          onAttendanceChatInputChange?.(next)
                        }}
                        externalInputRef={inputRef}
                        inputType={inputType === 'phone' ? 'phone' : 'text'}
                        phoneOptions={phoneOptions}
                        showAttachment={false}
                      />
                    </div>
                  </>
                ) : showAttendanceDateRange && onDateRangeSelect ? (
                  <ChatbotDateRangeInput
                    onDateRangeSelect={onDateRangeSelect}
                    theme={resolvedTheme}
                    maxDate={datePickerMaxDate}
                  />
                ) : showDatePicker &&
                  (datePickerMode === 'range' ? onDateRangeSelect : onDateSelect) ? (
                  datePickerMode === 'range' && onDateRangeSelect ? (
                    <ChatbotDatePicker
                      mode='range'
                      onDateRangeSelect={onDateRangeSelect}
                      onSkip={onSkipQuestion}
                      showSkip={!isDateQuestionRequired}
                      theme={resolvedTheme}
                      minDate={datePickerMinDate}
                      maxDate={datePickerMaxDate}
                    />
                  ) : (
                    <ChatbotDatePicker
                      onDateSelect={onDateSelect!}
                      onSkip={onSkipQuestion}
                      showSkip={!isDateQuestionRequired}
                      theme={resolvedTheme}
                      minDate={datePickerMinDate}
                      maxDate={datePickerMaxDate}
                    />
                  )
                ) : showSchoolSelect && onSchoolSelect ? (
                  <AttendanceSchoolSelect
                    options={schoolSelectOptions}
                    onSelect={onSchoolSelect}
                    theme={resolvedTheme}
                    placeholder={schoolSelectPlaceholder}
                    emptyMessage={schoolSelectEmptyMessage}
                  />
                ) : showAttendanceStudentMultiSelect &&
                  onConfirmAttendanceStudents &&
                  attendanceStudentCandidates.length > 0 ? (
                  <AttendanceStudentMultiSelect
                    candidates={attendanceStudentCandidates}
                    onConfirm={onConfirmAttendanceStudents}
                    theme={resolvedTheme}
                    disabled={isTyping}
                  />
                ) : showCountrySelect && onCountrySelect ? (
                  <CountrySelect
                    onSelect={onCountrySelect}
                    selectedValue={selectedCountry}
                    theme={resolvedTheme}
                  />
                ) : showBloodGroupSelect && onBloodGroupSelect ? (
                  <BloodGroupSelect
                    onSelect={onBloodGroupSelect}
                    selectedValue={selectedBloodGroup}
                    theme={resolvedTheme}
                  />
                ) : showFileUpload && onFileUpload ? (
                  <FileUploadInput onFileSelect={onFileUpload} disabled={isTyping} />
                ) : showOptionChips && onOptionSelect && optionChipsOptions.length > 0 ? (
                  <OptionChips
                    options={optionChipsOptions}
                    onSelect={onOptionSelect}
                    selectedValue={selectedOptionValue}
                    theme={resolvedTheme}
                    layout={optionChipsLayout}
                  />
                ) : disableChatInput ? null : (
                  <ChatInput
                    isActive={isOpen}
                    onSend={(text: string) => {
                      onMenuStateChange({
                        isMenuOpen: false,
                        isSubmenuOpen: false,
                        isConversationStarted: true,
                      })
                      setShowMenu(false)
                      setSelectedMenuItem(null)
                      setInputText('')
                      // Sending re-anchors the view: even if the reader scrolled up
                      // into history, their new prompt must come into view at the bottom.
                      forceScrollToBottom()
                      onSendMessage(text)
                    }}
                    onFocus={() => {
                      try {
                        setShowMenu(true)
                        onMenuStateChange({
                          isMenuOpen: true,
                          isSubmenuOpen: false,
                          isConversationStarted: false,
                        })
                      } catch (e) {
                        calculateErrorMessage(e)
                      }
                    }}
                    theme={resolvedTheme}
                    disabled={isTyping || isCallActive}
                    value={inputText}
                    onChangeValue={setInputText}
                    externalInputRef={inputRef}
                    inputType={inputType === 'phone' ? 'phone' : 'text'}
                    phoneOptions={phoneOptions}
                    showAttachment={!isFaqMode}
                  />
                )}
                <ChatbotPoweredBy />
              </div>
            </>
          )}

          {showVoiceOverlay && (
            <VoiceModePage
              status={callStatus}
              transcriptionEnabled={transcriptionEnabled}
              isMuted={callMuted}
              error={callError}
              transcript={transcript}
              interimUserTranscript={interimUserTranscript}
              micLevel={micLevel}
              awaitingResponse={awaitingResponse}
              onEndCall={endCall}
              onMuteToggle={toggleMute}
              onReturnToChat={returnToIdle}
              onRetry={retryCall}
            />
          )}
        </div>

        {showTranscriptionModal && <TranscriptionModal onChoice={chooseTranscription} />}
        {callStatus === 'choosing_voice' && (
          <VoiceSelectionModal
            voices={voices}
            initialSelectedVoice={selectedVoice}
            onChoice={(v) => chooseVoice(v)}
            onCancel={() => returnToIdle()}
          />
        )}
      </>
    )
  }
)

ChatbotWidget.displayName = 'ChatbotWidget'

export default ChatbotWidget
